import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { format, formatDistanceToNow, differenceInMinutes } from 'date-fns';
import { useParams, useNavigate } from '@tanstack/react-router';

import Page from '@/components/page/Page';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Bot,
  CheckCircle2,
  Loader2,
  XCircle,
  Clock,
  Package,
  TrendingDown,
  Activity,
  Pause,
  ArrowLeft,
  Calendar,
  Timer,
  Boxes,
  Truck,
  MessageSquare,
  Send,
  Users,
  DollarSign,
  AlertTriangle,
  FileText,
  Building2,
  Mail,
  Check,
  Lightbulb,
  Plus,
  Phone,
  Gift,
  Paperclip,
  ExternalLink,
  Play,
  Headphones,
  PhoneCall,
  PhoneOff,
  Mic,
  Volume2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useVapiCall } from '@/hooks/useVapiCall';

import {
  getBuyingAgentById,
  progressAgentToStep2,
  progressAgentToStep3,
  isNewlyCreatedAgent,
  type BuyingAgent,
  type AgentStep,
  type AnalysisData,
  type CommunicationData,
  type ApprovalData,
  type GenerationData,
  type ActiveCommunication,
  type EmailMessage,
  type CallRecording,
} from './buyingAgentsStore';

const statusConfig: Record<
  BuyingAgent['status'],
  {
    bg: string;
    text: string;
    label: string;
    icon: React.ReactNode;
    border: string;
  }
> = {
  running: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    label: 'Running',
    icon: <Loader2 className="h-4 w-4 animate-spin" />,
    border: 'border-blue-200',
  },
  waiting_approval: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    label: 'Awaiting Approval',
    icon: <Clock className="h-4 w-4" />,
    border: 'border-amber-200',
  },
  completed: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    label: 'Completed',
    icon: <CheckCircle2 className="h-4 w-4" />,
    border: 'border-green-200',
  },
  failed: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    label: 'Failed',
    icon: <XCircle className="h-4 w-4" />,
    border: 'border-red-200',
  },
  paused: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    label: 'Paused',
    icon: <Pause className="h-4 w-4" />,
    border: 'border-gray-200',
  },
};

const stepStatusConfig: Record<
  AgentStep['status'],
  { bg: string; border: string; icon: React.ReactNode; iconBg: string }
> = {
  pending: {
    bg: 'bg-transparent',
    border: 'border-border',
    icon: <Clock className="h-5 w-5 text-muted-foreground" />,
    iconBg: 'bg-muted',
  },
  running: {
    bg: 'bg-transparent',
    border: 'border-blue-200',
    icon: <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />,
    iconBg: 'bg-blue-100',
  },
  completed: {
    bg: 'bg-transparent',
    border: 'border-green-300',
    icon: <CheckCircle2 className="h-5 w-5 text-green-600" />,
    iconBg: 'bg-green-100',
  },
  failed: {
    bg: 'bg-transparent',
    border: 'border-red-200',
    icon: <XCircle className="h-5 w-5 text-red-600" />,
    iconBg: 'bg-red-100',
  },
};

const stepTypeIcons: Record<string, React.ReactNode> = {
  analyze: <Bot className="h-4 w-4" />,
  communicate: <MessageSquare className="h-4 w-4" />,
  approval: <Users className="h-4 w-4" />,
  generate: <Send className="h-4 w-4" />,
};

export function AIWorkflowDetailsPage() {
  const { agentId } = useParams({ strict: false }) as { agentId: string };
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedEmail, setSelectedEmail] =
    useState<ActiveCommunication | null>(null);
  const [selectedCallRecording, setSelectedCallRecording] = useState<{
    supplier: string;
    recording: CallRecording;
  } | null>(null);
  const [expandedEmailIds, setExpandedEmailIds] = useState<Set<string>>(
    new Set(),
  );

  const handleSelectEmail = useCallback((comm: ActiveCommunication | null) => {
    setSelectedEmail(comm);
    // Expand the latest email (last in thread) by default
    if (comm?.emailThread && comm.emailThread.length > 0) {
      const latestEmailId = comm.emailThread[comm.emailThread.length - 1].id;
      setExpandedEmailIds(new Set([latestEmailId]));
    } else {
      setExpandedEmailIds(new Set());
    }
  }, []);

  const toggleEmailExpanded = useCallback((emailId: string) => {
    setExpandedEmailIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  }, []);

  // Vapi call management - for making outbound phone calls
  const { supplierCalls, startCall, endCall, isAnyCallActive } = useVapiCall();

  const agent = useMemo(() => {
    // refreshKey dependency ensures we re-fetch after progression
    void refreshKey;
    return getBuyingAgentById(agentId);
  }, [agentId, refreshKey]);

  // Check if this is a newly created agent on mount
  const isNewAgent = useMemo(() => {
    return isNewlyCreatedAgent(agentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]); // Only check once when agentId changes

  // Simple timer: fire once after 5 seconds for newly created agents
  useEffect(() => {
    // Only run for newly created agents
    if (!isNewAgent) return;

    // Set a 5 second timer
    const timer = setTimeout(() => {
      progressAgentToStep2(agentId);
      setRefreshKey((k) => k + 1);
    }, 3500);

    // Cleanup on unmount
    return () => clearTimeout(timer);
  }, [agentId, isNewAgent]);

  // Track if we've already progressed after call end to avoid duplicate triggers
  const hasProgressedAfterCall = useRef(false);
  const progressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Watch for call ending - progress to approval step after 3 seconds
  useEffect(() => {
    // Check if any call just ended
    const calls = Array.from(supplierCalls.values());
    const endedCall = calls.find((c) => c.status === 'ended');

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/57d3a4af-f82d-40f1-b1ca-c3d150fa6904', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        location: 'AIWorkflowDetailsPage.tsx:useEffect-callEnd',
        message: 'Checking for ended call',
        data: {
          callCount: calls.length,
          statuses: calls.map((c) => c.status),
          hasEndedCall: !!endedCall,
          hasProgressedAlready: hasProgressedAfterCall.current,
          hasTimer: !!progressTimerRef.current,
        },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        hypothesisId: 'H1,H2',
      }),
    }).catch(() => {});
    // #endregion

    console.log('🔍 Checking for ended call:', {
      callCount: calls.length,
      statuses: calls.map((c) => c.status),
      hasEndedCall: !!endedCall,
      hasProgressedAlready: hasProgressedAfterCall.current,
      hasTimer: !!progressTimerRef.current,
    });

    // Only set timer once when we first detect "ended" status
    if (
      endedCall &&
      !hasProgressedAfterCall.current &&
      !progressTimerRef.current
    ) {
      console.log('✅ Found ended call, will progress in 3 seconds...');
      hasProgressedAfterCall.current = true;

      // #region agent log
      fetch(
        'http://127.0.0.1:7242/ingest/57d3a4af-f82d-40f1-b1ca-c3d150fa6904',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: 'AIWorkflowDetailsPage.tsx:settingTimer',
            message: 'Setting 3s timer for progression',
            data: { agentId },
            timestamp: Date.now(),
            sessionId: 'debug-session',
            hypothesisId: 'H1',
          }),
        },
      ).catch(() => {});
      // #endregion

      // Store timer in ref so it persists across effect re-runs
      progressTimerRef.current = setTimeout(() => {
        console.log('⏱️ Timer fired, progressing to step 3...');

        // #region agent log
        fetch(
          'http://127.0.0.1:7242/ingest/57d3a4af-f82d-40f1-b1ca-c3d150fa6904',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              location: 'AIWorkflowDetailsPage.tsx:timerFired',
              message: 'Timer fired, calling progressAgentToStep3',
              data: { agentId },
              timestamp: Date.now(),
              sessionId: 'debug-session',
              hypothesisId: 'H1,H3',
            }),
          },
        ).catch(() => {});
        // #endregion

        const success = progressAgentToStep3(agentId);
        console.log('📋 progressAgentToStep3 result:', success);

        // #region agent log
        fetch(
          'http://127.0.0.1:7242/ingest/57d3a4af-f82d-40f1-b1ca-c3d150fa6904',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              location: 'AIWorkflowDetailsPage.tsx:progressResult',
              message: 'progressAgentToStep3 completed',
              data: { agentId, success },
              timestamp: Date.now(),
              sessionId: 'debug-session',
              hypothesisId: 'H3',
            }),
          },
        ).catch(() => {});
        // #endregion

        setRefreshKey((k) => k + 1);
        progressTimerRef.current = null;
      }, 3000);
    }

    // Cleanup only on unmount, not on every re-render
    return () => {
      // Don't clear timer on re-render, only on actual unmount
    };
  }, [supplierCalls, agentId]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
  }, []);

  if (!agent) {
    return (
      <Page title="Agent Not Found">
        <div className="flex flex-col items-center justify-center py-12">
          <Bot className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Agent not found</h2>
          <p className="text-muted-foreground mb-4">
            The requested agent could not be found.
          </p>
          <Button
            onClick={() => navigate({ to: '/procurement-demo/ai-workflows' })}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Agents
          </Button>
        </div>
      </Page>
    );
  }

  const status = statusConfig[agent.status];

  // Calculate duration
  const getDuration = () => {
    const start = new Date(agent.startedAt);
    const end = agent.completedAt ? new Date(agent.completedAt) : new Date();
    const minutes = differenceInMinutes(end, start);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const renderStepDetails = (step: AgentStep) => {
    if (!step.details?.data) return null;

    switch (step.details.type) {
      case 'analysis': {
        const data = step.details.data as AnalysisData;
        return (
          <div className="mt-2 text-sm text-muted-foreground">
            {data.vendorsEvaluated !== undefined && data.topCandidates && (
              <span>
                Evaluated {data.vendorsEvaluated} vendors • Top picks:{' '}
                {data.topCandidates.slice(0, 3).join(', ')}
              </span>
            )}
          </div>
        );
      }

      case 'communication': {
        const data = step.details.data as CommunicationData;
        return (
          <div className="mt-3 space-y-3 text-sm">
            {data.rfqsSent !== undefined && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4" />
                <span>
                  {data.rfqsSent} RFQs sent, {data.responsesReceived ?? 0}{' '}
                  responses received
                </span>
              </div>
            )}

            {/* Active Communications */}
            {data.activeCommunications &&
              data.activeCommunications.length > 0 && (
                <div className="mt-4 space-y-3">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Active Communications
                  </div>
                  {data.activeCommunications.map(
                    (comm: ActiveCommunication) => {
                      const hasEmailContent =
                        comm.type === 'email' &&
                        (comm.emailContent || comm.emailThread);
                      const hasCallRecording =
                        comm.type === 'call' && comm.callRecording;

                      return (
                        <div key={comm.id} className="space-y-2">
                          <button
                            type="button"
                            className={`w-full flex items-start gap-3 p-3 rounded-lg bg-background border text-left transition-all ${
                              hasEmailContent || hasCallRecording
                                ? 'hover:border-blue-300 hover:shadow-sm cursor-pointer'
                                : ''
                            }`}
                            onClick={() => {
                              if (hasEmailContent) {
                                handleSelectEmail(comm);
                              } else if (
                                hasCallRecording &&
                                comm.callRecording
                              ) {
                                setSelectedCallRecording({
                                  supplier: comm.supplier,
                                  recording: comm.callRecording,
                                });
                              }
                            }}
                            disabled={!hasEmailContent && !hasCallRecording}
                          >
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                comm.type === 'email'
                                  ? 'bg-blue-100 text-blue-600'
                                  : comm.type === 'call'
                                    ? comm.status === 'completed'
                                      ? 'bg-green-100 text-green-600'
                                      : 'bg-emerald-100 text-emerald-600'
                                    : 'bg-violet-100 text-violet-600'
                              }`}
                            >
                              {comm.type === 'email' ? (
                                <Mail className="h-4 w-4" />
                              ) : comm.type === 'call' ? (
                                comm.status === 'completed' ? (
                                  <Headphones className="h-4 w-4" />
                                ) : (
                                  <Phone className="h-4 w-4" />
                                )
                              ) : (
                                <Users className="h-4 w-4" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium">
                                  {comm.supplier}
                                </span>
                                <div className="flex items-center gap-2">
                                  {hasEmailContent && (
                                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                                  )}
                                  {hasCallRecording && (
                                    <Play className="h-3 w-3 text-muted-foreground" />
                                  )}
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${
                                      comm.status === 'in_progress'
                                        ? 'bg-green-50 text-green-700 border-green-200'
                                        : comm.status === 'awaiting_response'
                                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                                          : comm.status === 'responded'
                                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                                            : comm.status === 'completed'
                                              ? 'bg-green-50 text-green-700 border-green-200'
                                              : 'bg-gray-50 text-gray-700 border-gray-200'
                                    }`}
                                  >
                                    {comm.status === 'in_progress' && (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    )}
                                    {comm.status === 'completed' && (
                                      <CheckCircle2 className="h-3 w-3 mr-1" />
                                    )}
                                    {comm.status === 'in_progress'
                                      ? 'On Call'
                                      : comm.status === 'awaiting_response'
                                        ? 'Awaiting Response'
                                        : comm.status === 'responded'
                                          ? 'Responded'
                                          : comm.status === 'completed'
                                            ? 'Completed'
                                            : 'Sent'}
                                  </Badge>
                                </div>
                              </div>
                              {/* Summary */}
                              {comm.summary && (
                                <div className="text-sm text-muted-foreground flex items-start gap-1.5">
                                  {comm.summary !==
                                    'No response from supplier' && (
                                    <Bot className="h-3.5 w-3.5 text-violet-500 shrink-0 mt-0.5" />
                                  )}
                                  <span>{comm.summary}</span>
                                </div>
                              )}
                              {/* Price and Lead Time */}
                              {(comm.quotedPrice || comm.quotedLeadTime) && (
                                <div className="flex items-center gap-3 mt-1.5 text-xs">
                                  {comm.quotedPrice && (
                                    <span className="font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">
                                      ${comm.quotedPrice.toLocaleString()}
                                    </span>
                                  )}
                                  {comm.quotedLeadTime && (
                                    <span className="text-muted-foreground">
                                      {comm.quotedLeadTime}
                                    </span>
                                  )}
                                </div>
                              )}
                              {/* Call recording indicator */}
                              {hasCallRecording && comm.callRecording && (
                                <div className="flex items-center gap-2 mt-1.5 text-xs text-green-700">
                                  <Play className="h-3 w-3" />
                                  <span>
                                    Recording ({comm.callRecording.duration})
                                  </span>
                                </div>
                              )}
                            </div>
                          </button>

                          {/* Volume Discount Offer Card */}
                          {comm.volumeDiscountOffer && (
                            <div className="ml-11 p-3 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200">
                              <div className="flex items-start gap-2">
                                <Gift className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-emerald-800">
                                    Volume Discount Available!
                                  </div>
                                  <p className="text-xs text-emerald-700 mt-1">
                                    {comm.supplier} is offering{' '}
                                    <span className="font-semibold">
                                      {comm.volumeDiscountOffer.discountPercent}
                                      % off
                                    </span>{' '}
                                    if you also order{' '}
                                    <span className="font-semibold">
                                      {comm.volumeDiscountOffer.product}
                                    </span>{' '}
                                    (min. {comm.volumeDiscountOffer.minQuantity}{' '}
                                    units)
                                  </p>
                                  <div className="flex gap-2 mt-2">
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      className="h-7 text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-700"
                                    >
                                      <Plus className="h-3 w-3 mr-1" />
                                      Add to Order
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 text-xs text-emerald-600"
                                    >
                                      View Details
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    },
                  )}

                  {/* Call Supplier Button - shown if there are suppliers awaiting response */}
                  {(() => {
                    const awaitingSuppliers = data.activeCommunications?.filter(
                      (c) =>
                        c.status === 'awaiting_response' && c.supplierPhone,
                    );
                    if (!awaitingSuppliers || awaitingSuppliers.length === 0)
                      return null;

                    // Just use the first supplier for simplicity
                    const firstSupplier = awaitingSuppliers[0];
                    const callState = supplierCalls.get(firstSupplier.id);
                    const hasActiveCall =
                      callState &&
                      (callState.status === 'active' ||
                        callState.status === 'connecting');

                    const handleCallSupplier = () => {
                      startCall({
                        id: firstSupplier.id,
                        name: firstSupplier.supplier,
                        phone: firstSupplier.supplierPhone,
                      });
                    };

                    return (
                      <div className="mt-4 space-y-3">
                        {/* Call button section */}
                        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-amber-600" />
                              <span className="text-sm text-amber-800">
                                {firstSupplier.supplier} hasn't responded yet
                              </span>
                            </div>
                            {hasActiveCall ? (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => endCall(firstSupplier.id)}
                              >
                                <PhoneOff className="h-4 w-4 mr-2" />
                                End Call
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={handleCallSupplier}
                                disabled={isAnyCallActive}
                              >
                                <PhoneCall className="h-4 w-4 mr-2" />
                                Call {firstSupplier.supplier}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

            {data.negotiations && (
              <div className="mt-2 space-y-1">
                <span className="text-muted-foreground">Negotiations:</span>
                {data.negotiations.map((n) => (
                  <div
                    key={n.supplier}
                    className="flex items-center gap-2 ml-4"
                  >
                    <Building2 className="h-3 w-3 text-muted-foreground" />
                    <span>{n.supplier}:</span>
                    <span className="text-green-600 font-medium">
                      -{n.discount}%
                    </span>
                  </div>
                ))}
              </div>
            )}
            {data.selectedSupplier && (
              <div className="flex items-center gap-2 text-green-600 font-medium">
                <CheckCircle2 className="h-4 w-4" />
                <span>Selected: {data.selectedSupplier}</span>
              </div>
            )}
            {data.currentNegotiation && (
              <div className="flex items-center gap-2 text-blue-600">
                <MessageSquare className="h-4 w-4" />
                <span>{data.currentNegotiation}</span>
              </div>
            )}
            {data.failureReason && (
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-4 w-4" />
                <span>{data.failureReason}</span>
              </div>
            )}
          </div>
        );
      }

      case 'approval': {
        const data = step.details.data as ApprovalData;
        return (
          <div className="mt-3">
            {/* Recommendation Card */}
            {data.recommendedSupplier && step.status === 'running' && (
              <div className="inline-block border border-gray-200 rounded-lg bg-white min-w-[420px] shadow-sm">
                {/* Header */}
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-violet-50/50">
                  <Bot className="h-4 w-4 text-violet-600" />
                  <span className="text-sm font-medium text-gray-800">
                    AI Recommendation
                  </span>
                </div>

                {/* Content */}
                <div className="p-4 space-y-2.5">
                  {/* Supplier */}
                  <div className="flex items-center justify-between gap-6">
                    <span className="text-sm text-muted-foreground">
                      Supplier
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {data.recommendedSupplier}
                    </span>
                  </div>
                  {data.quantity && (
                    <div className="flex items-center justify-between gap-6">
                      <span className="text-sm text-muted-foreground">
                        Quantity
                      </span>
                      <span className="text-sm text-gray-900">
                        {data.quantity.toLocaleString()} units
                      </span>
                    </div>
                  )}
                  {data.unitPrice && (
                    <div className="flex items-center justify-between gap-6">
                      <span className="text-sm text-muted-foreground">
                        Unit Price
                      </span>
                      <span className="text-sm text-gray-900">
                        ${data.unitPrice.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {data.leadTime && (
                    <div className="flex items-center justify-between gap-6">
                      <span className="text-sm text-muted-foreground">
                        Lead Time
                      </span>
                      <span className="text-sm text-gray-900">
                        {data.leadTime}
                      </span>
                    </div>
                  )}

                  {/* Total */}
                  {data.totalPrice && (
                    <div className="flex items-center justify-between gap-6 pt-2.5 mt-2.5 border-t border-gray-200">
                      <span className="text-sm font-medium text-gray-700">
                        Total
                      </span>
                      <span className="text-base font-bold text-gray-900">
                        ${data.totalPrice.toLocaleString()}
                      </span>
                    </div>
                  )}

                  {/* Action Button */}
                  <Button
                    className="w-full h-9 text-sm mt-3 bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={() => {
                      window.location.href = `/procurement-demo/purchase-orders?showAiOrder=true`;
                    }}
                  >
                    <Bot className="h-4 w-4 mr-2" />
                    Create Purchase Order
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'generation': {
        const data = step.details.data as GenerationData;
        return (
          <div className="mt-3 space-y-2 text-sm">
            {data.poNumber && (
              <div className="flex items-center gap-2 text-green-600 font-medium">
                <FileText className="h-4 w-4" />
                <span>Generated: {data.poNumber}</span>
              </div>
            )}
            {data.sentTo && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Send className="h-4 w-4" />
                <span>Sent to: {data.sentTo}</span>
              </div>
            )}
            {data.totalValue !== undefined && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <DollarSign className="h-4 w-4" />
                <span>Total value: ${data.totalValue.toLocaleString()}</span>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Page title="Agent Details">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: '/procurement-demo/ai-workflows' })}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <Bot className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold">Buying Agent</span>
                <Badge className={`${status.bg} ${status.text} border-0`}>
                  {status.icon}
                  <span className="ml-1">{status.label}</span>
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                {agent.requestNumber}
              </div>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Started</div>
                <div className="text-sm font-semibold">
                  {format(new Date(agent.startedAt), 'MMM d, h:mm a')}
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Product(s)</div>
                <div className="text-sm font-semibold">{agent.product}</div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Boxes className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Quantity</div>
                <div className="text-sm font-semibold">
                  {agent.quantity.toLocaleString()} units
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Lead Time</div>
                <div className="text-sm font-semibold">
                  {agent.leadTimeDays} days
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <Timer className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Duration</div>
                <div className="text-sm font-semibold">{getDuration()}</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-3 gap-6">
          {/* Execution Process */}
          <div className="col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="h-5 w-5 text-violet-600" />
                    Execution Process
                  </CardTitle>
                  {/* Compact Order Summary */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5" />
                      {agent.requestNumber}
                    </span>
                    <span className="text-border">|</span>
                    <span>{agent.product}</span>
                    <span className="text-border">|</span>
                    <span>{agent.quantity.toLocaleString()} units</span>
                    <span className="text-border">|</span>
                    <span className="font-medium">
                      ${agent.estimatedValue.toLocaleString()}
                    </span>
                    {agent.supplierSelected && (
                      <>
                        <span className="text-border">|</span>
                        <span className="flex items-center gap-1 text-green-600">
                          <Building2 className="h-3.5 w-3.5" />
                          {agent.supplierSelected}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

                  <div className="space-y-6">
                    {agent.steps
                      .filter((step) => step.id !== 'generate') // Hide Generate Purchase Order step
                      .map((step) => {
                        const stepConfig = stepStatusConfig[step.status];
                        // Allow clicking approval step to skip to it (when pending and communicate is running)
                        const canSkipToStep =
                          step.id === 'approval' &&
                          step.status === 'pending' &&
                          agent.steps.find((s) => s.id === 'communicate')
                            ?.status === 'running';

                        const handleSkipToStep = () => {
                          if (canSkipToStep) {
                            progressAgentToStep3(agentId);
                            setRefreshKey((k) => k + 1);
                          }
                        };

                        return (
                          <div key={step.id} className="relative pl-16">
                            {/* Timeline dot - clickable for approval step to skip */}
                            <div
                              className={`absolute left-3.5 w-5 h-5 rounded-full ${stepConfig.iconBg} flex items-center justify-center ring-4 ring-background ${canSkipToStep ? 'cursor-pointer hover:ring-violet-300 hover:scale-110 transition-all' : ''}`}
                              onClick={handleSkipToStep}
                              title={
                                canSkipToStep
                                  ? 'Click to skip to this step'
                                  : ''
                              }
                            >
                              {stepTypeIcons[step.id] || stepConfig.icon}
                            </div>

                            {/* Step card */}
                            <div
                              className={`p-4 rounded-lg border ${stepConfig.border} ${stepConfig.bg}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold">
                                      {step.name}
                                    </h4>
                                    {step.status === 'running' && (
                                      <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">
                                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                        In Progress
                                      </Badge>
                                    )}
                                    {step.status === 'completed' && (
                                      <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                                        <CheckCircle2 className="h-3 w-3 mr-1" />
                                        Completed
                                      </Badge>
                                    )}
                                    {step.status === 'failed' && (
                                      <Badge className="bg-red-100 text-red-700 border-0 text-xs">
                                        <XCircle className="h-3 w-3 mr-1" />
                                        Failed
                                      </Badge>
                                    )}
                                    {step.status === 'pending' && (
                                      <>
                                        <Badge
                                          variant="outline"
                                          className="text-xs text-muted-foreground"
                                        >
                                          <Clock className="h-3 w-3 mr-1" />
                                          Pending
                                        </Badge>
                                        {canSkipToStep && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-5 text-[10px] px-1.5 text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                                            onClick={handleSkipToStep}
                                          >
                                            Skip to this step →
                                          </Button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {step.description}
                                  </p>

                                  {/* Step timing */}
                                  {step.startedAt && (
                                    <div className="text-xs text-muted-foreground mt-2">
                                      {step.status === 'running' ? (
                                        <span>
                                          Started{' '}
                                          {formatDistanceToNow(
                                            new Date(step.startedAt),
                                            { addSuffix: true },
                                          )}
                                        </span>
                                      ) : step.completedAt ? (
                                        <span>
                                          Completed in{' '}
                                          {differenceInMinutes(
                                            new Date(step.completedAt),
                                            new Date(step.startedAt),
                                          )}{' '}
                                          min
                                        </span>
                                      ) : null}
                                    </div>
                                  )}

                                  {/* Step details */}
                                  {renderStepDetails(step)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Live Call Card - shown when a call is active */}
            {(() => {
              const activeCall = Array.from(supplierCalls.values()).find(
                (c) => c.status !== 'idle',
              );
              if (!activeCall) return null;

              const supplierId = Array.from(supplierCalls.entries()).find(
                ([_, v]) => v === activeCall,
              )?.[0];

              return (
                <Card className="border-emerald-200">
                  <CardHeader className="pb-2 pt-3 px-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2">
                        {activeCall.status === 'active' ||
                        activeCall.status === 'connecting' ||
                        activeCall.status === 'ringing' ? (
                          <>
                            <span className="relative flex h-2.5 w-2.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                            </span>
                            <span className="text-emerald-700">Live Call</span>
                          </>
                        ) : activeCall.status === 'ended' ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            <span>Call Ended</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="text-red-700">Call Error</span>
                          </>
                        )}
                      </CardTitle>
                      {(activeCall.status === 'active' ||
                        activeCall.status === 'connecting' ||
                        activeCall.status === 'ringing') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => supplierId && endCall(supplierId)}
                        >
                          <PhoneOff className="h-3 w-3 mr-1" />
                          End
                        </Button>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {activeCall.supplierName}
                    </div>
                  </CardHeader>
                  <CardContent className="px-3 pb-3 pt-0">
                    <div className="max-h-[calc(100vh-500px)] overflow-y-auto space-y-1.5">
                      {activeCall.transcript.length === 0 &&
                        activeCall.status === 'connecting' && (
                          <div className="text-center py-3 text-xs text-muted-foreground">
                            <Loader2 className="h-4 w-4 mx-auto mb-1 text-emerald-500 animate-spin" />
                            Connecting...
                          </div>
                        )}
                      {activeCall.transcript.length === 0 &&
                        activeCall.status === 'active' && (
                          <div className="text-center py-3 text-xs text-muted-foreground">
                            <Mic className="h-4 w-4 mx-auto mb-1 text-emerald-500 animate-pulse" />
                            Listening...
                          </div>
                        )}
                      {activeCall.transcript.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`rounded-md p-1.5 text-xs ${
                            msg.role === 'assistant'
                              ? 'bg-violet-50 border border-violet-100'
                              : 'bg-emerald-50 border border-emerald-100'
                          }`}
                        >
                          <div className="flex items-center gap-1 mb-0.5">
                            {msg.role === 'assistant' ? (
                              <Bot className="h-3 w-3 text-violet-600" />
                            ) : (
                              <Volume2 className="h-3 w-3 text-emerald-600" />
                            )}
                            <span className="font-medium text-[10px]">
                              {msg.role === 'assistant' ? 'AI' : 'You'}
                            </span>
                          </div>
                          <p className="text-muted-foreground leading-snug">
                            {msg.text}
                          </p>
                        </div>
                      ))}
                      {activeCall.status === 'ended' &&
                        activeCall.transcript.length > 0 && (
                          <div className="text-center py-1">
                            <span className="text-[10px] text-muted-foreground bg-gray-100 rounded-full px-2 py-0.5">
                              Call ended
                            </span>
                          </div>
                        )}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Email Thread Card - shown when email is selected */}
            {selectedEmail && (
              <Card>
                <CardHeader className="pb-2 pt-3 px-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Mail className="h-4 w-4 text-blue-600" />
                      {selectedEmail.supplier}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                      onClick={() => handleSelectEmail(null)}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {selectedEmail.subject}
                  </div>
                </CardHeader>
                <CardContent className="px-3 pb-3 pt-0">
                  <div className="space-y-2">
                    {selectedEmail.emailThread?.map(
                      (message: EmailMessage, idx: number) => {
                        const isReply = message.isReply;
                        const isExpanded = expandedEmailIds.has(message.id);
                        return (
                          <div
                            key={message.id}
                            className={`rounded-md text-xs overflow-hidden ${
                              isReply
                                ? 'border border-blue-200'
                                : 'border border-border'
                            }`}
                          >
                            {/* Clickable header */}
                            <button
                              type="button"
                              onClick={() => toggleEmailExpanded(message.id)}
                              className="w-full p-2 flex items-center gap-2 text-left hover:bg-black/5 transition-colors"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                              ) : (
                                <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                              )}
                              <span className="font-medium truncate flex-1">
                                {message.from.split('@')[0]}
                              </span>
                              <span className="text-muted-foreground text-[10px] shrink-0">
                                {format(new Date(message.sentAt), 'h:mm a')}
                              </span>
                            </button>

                            {/* Collapsed preview */}
                            {!isExpanded && (
                              <div className="px-2 pb-2 -mt-1">
                                <p className="text-muted-foreground line-clamp-1 pl-5">
                                  {message.body
                                    .split('\n')
                                    .filter(
                                      (line) =>
                                        line.includes('$') ||
                                        line.includes('Total') ||
                                        line.includes('Lead Time') ||
                                        line.includes('Quantity') ||
                                        line.includes('approved') ||
                                        line.includes('Approved') ||
                                        line.includes('quote') ||
                                        line.includes('Quote'),
                                    )
                                    .slice(0, 1)
                                    .join('') ||
                                    message.body.slice(0, 50) + '...'}
                                </p>
                              </div>
                            )}

                            {/* Expanded content */}
                            {isExpanded && (
                              <div className="px-2 pb-2 border-t border-inherit">
                                <div className="text-[10px] text-muted-foreground py-1 mb-1">
                                  To: {message.to}
                                </div>
                                <div className="whitespace-pre-wrap text-xs leading-relaxed text-foreground max-h-48 overflow-y-auto">
                                  {message.body}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                  {/* Status */}
                  <div
                    className={`rounded-md p-2 mt-2 flex items-center gap-2 ${
                      selectedEmail.status === 'awaiting_response'
                        ? 'bg-amber-50 text-amber-700'
                        : selectedEmail.status === 'responded'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-blue-50 text-blue-700'
                    }`}
                  >
                    {selectedEmail.status === 'awaiting_response' ? (
                      <>
                        <Clock className="h-3 w-3" />
                        <span className="text-xs">Awaiting response</span>
                      </>
                    ) : selectedEmail.status === 'responded' ? (
                      <>
                        <CheckCircle2 className="h-3 w-3" />
                        <span className="text-xs">Response received</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-3 w-3" />
                        <span className="text-xs">Sent</span>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Call Recording Card - shown when call recording is selected */}
            {selectedCallRecording && (
              <Card>
                <CardHeader className="pb-2 pt-3 px-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Headphones className="h-4 w-4 text-green-600" />
                      {selectedCallRecording.supplier}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                      onClick={() => setSelectedCallRecording(null)}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Call Recording • {selectedCallRecording.recording.duration}
                  </div>
                </CardHeader>
                <CardContent className="px-3 pb-3 pt-0">
                  {/* Mini Audio Player */}
                  <div className="flex items-center gap-2 p-2 rounded-md bg-green-50 border border-green-100 mb-2">
                    <button
                      type="button"
                      className="w-7 h-7 rounded-full bg-green-600 hover:bg-green-700 flex items-center justify-center text-white transition-colors shrink-0"
                    >
                      <Play className="h-3 w-3 ml-0.5" />
                    </button>
                    <div className="flex-1">
                      <div className="h-1.5 bg-green-200 rounded-full overflow-hidden">
                        <div className="h-full w-0 bg-green-600 rounded-full" />
                      </div>
                    </div>
                    <span className="text-[10px] text-green-700">
                      {selectedCallRecording.recording.duration}
                    </span>
                  </div>

                  {/* Summary */}
                  <div className="text-xs">
                    <div className="font-medium mb-1 text-muted-foreground flex items-center gap-1">
                      <Bot className="h-3 w-3 text-violet-500" />
                      Summary
                    </div>
                    <p className="text-muted-foreground bg-muted/50 rounded-md p-2">
                      {selectedCallRecording.recording.summary}
                    </p>
                  </div>

                  {/* Transcript */}
                  {selectedCallRecording.recording.transcript && (
                    <div className="text-xs mt-3">
                      <div className="font-medium mb-1 text-muted-foreground">
                        Transcript
                      </div>
                      <div className="bg-muted/30 rounded-md p-2 max-h-48 overflow-y-auto">
                        <pre className="text-[10px] text-muted-foreground whitespace-pre-wrap font-sans leading-relaxed">
                          {selectedCallRecording.recording.transcript}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Status */}
                  <div className="rounded-md p-2 mt-2 flex items-center gap-2 bg-green-50 text-green-700">
                    <CheckCircle2 className="h-3 w-3" />
                    <span className="text-xs">Call completed</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Savings Card */}
            {agent.savings && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        Negotiated Savings
                      </span>
                    </div>
                    <span className="text-xl font-bold text-green-600">
                      -${agent.savings.toLocaleString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
}
